import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DevicesContext } from '../../../contexts/DevicesContext';
import {
  deviceApi,
  SendWifiCredentialsRequest,
  UpdateDeviceRequest,
  UpdateDeviceStatusRequest,
} from '../../../services/deviceApi';
import type {
  BmsProtocol,
  FlowCtx,
  ModbusConnectionType,
  StepKey,
  ValidationErrors,
  WifiNetwork,
} from './types';

const featureBmsEnabled = process.env.REACT_APP_FEATURE_BMS === 'true';
const featureMqttEnabled = process.env.REACT_APP_FEATURE_MQTT === 'true';
// const enablePingCheck = process.env.REACT_APP_ENABLE_PING_CHECK === 'true';

const DEFAULT_OFFSETS = [
  { param: 'PM2.5', x: 1.0, y: 0.0 },
  { param: 'PM10', x: 1.0, y: 0.0 },
  { param: 'CO', x: 1.0, y: 0.0 },
  { param: 'SO₂', x: 1.0, y: 0.0 },
  { param: 'NO₂', x: 1.0, y: 0.0 },
  { param: 'O₃', x: 1.0, y: 0.0 },
  { param: 'CO₂', x: 1.0, y: 0.0 },
  { param: 'Humidity', x: 1.0, y: 0.0 },
  { param: 'Temperature', x: 1.0, y: 0.0 },
  { param: 'CH₂O', x: 1.0, y: 0.0 },
  { param: 'VOC', x: 1.0, y: 0.0 },
  { param: 'Noise', x: 1.0, y: 0.0 },
  { param: 'C₆H₆', x: 1.0, y: 0.0 },
];

const VALID_STEPS: StepKey[] = [
  'on',
  'set',
  'wificonf',
  'integrations',
  'deviceWifi',
  'connect',
  'wait',
  'done',
  'failed',
];

interface UseDeviceConfigFlowArgs {
  setShowConfigFlow: React.Dispatch<React.SetStateAction<boolean>>;
  SerialNumber: string;
  deviceId?: string;
}

interface UseDeviceConfigFlowResult {
  ctx: FlowCtx;
  popup: {
    isOpen: boolean;
    status: 'success' | 'failed';
    title: string;
    subtitle: string;
    actionText?: string;
    onAction?: () => void;
    variant: 'default' | 'device';
    showAlertIcon?: boolean;
  };
  confirmPopup: {
    isOpen: boolean;
  };
  closePopup: () => void;
  closeConfirmPopup: () => void;
}

export function useDeviceConfigFlow({
  setShowConfigFlow,
  SerialNumber,
  deviceId,
}: UseDeviceConfigFlowArgs): UseDeviceConfigFlowResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState<StepKey>('on');
  const stepRef = useRef<StepKey>('on');
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // Track whether this is a first-time onboarding (REGISTERED + UNKNOWN)
  // vs. re-onboarding an already-active device.
  const [isFirstOnboarding, setIsFirstOnboarding] = useState(true);
  const isFirstOnboardingRef = useRef(true);
  useEffect(() => {
    let cancelled = false;
    deviceApi.getDeviceBySerialNumber(SerialNumber).then((device) => {
      if (cancelled) return;
      const onboarding = (device.onboardingStatus ?? '').toUpperCase();
      const operational = (device.operationalStatus ?? '').toUpperCase();
      const firstTime = onboarding === 'REGISTERED' && operational === 'UNKNOWN';
      isFirstOnboardingRef.current = firstTime;
      setIsFirstOnboarding(firstTime);
    }).catch(() => {
      isFirstOnboardingRef.current = true;
      setIsFirstOnboarding(true);
    });
    return () => { cancelled = true; };
  }, [SerialNumber]);

  // Loading is scoped to the step that initiated it. When the user navigates
  // back, the spinner stays bound to its owner step and does not bleed into
  // earlier steps that read `ctx.loading`.
  const [loadingStep, setLoadingStep] = useState<StepKey | null>(null);
  const loading = loadingStep === step;

  const startLoading = useCallback(() => {
    setLoadingStep(stepRef.current);
  }, []);
  const stopLoading = useCallback(() => {
    setLoadingStep(null);
  }, []);

  // Wi-Fi & server form state
  const [wifiname, setWifiName] = useState('');
  const [wifipassword, setWifiPassword] = useState('');
  const wifinnameRef = useRef(wifiname);
  useEffect(() => { wifinnameRef.current = wifiname; }, [wifiname]);
  const wifipasswordRef = useRef(wifipassword);
  useEffect(() => { wifipasswordRef.current = wifipassword; }, [wifipassword]);
  const [serverUrl, setServerUrl] = useState(
    'https://app.smarteco.ai/smartecodeviceapi',
  );

  // BMS
  const [enableBms, setEnableBms] = useState(false);
  const [bmsProtocol, setBmsProtocol] = useState<BmsProtocol>('bacnet');
  const [bacnetDeviceId, setBacnetDeviceId] = useState('');
  const [bacnetDeviceName, setBacnetDeviceName] = useState('');
  const [bacnetPort, setBacnetPort] = useState('47808');
  const [modbusPort, setModbusPort] = useState('502');
  const [modbusDeviceId, setModbusDeviceId] = useState('');
  const [modbusConnectionType, setModbusConnectionType] =
    useState<ModbusConnectionType>('TCP');

  // MQTT
  const [enableMqtt, setEnableMqtt] = useState(false);
  const [mqttHost, setMqttHost] = useState('');
  const [mqttPort, setMqttPort] = useState('1883');
  const [mqttSsl, setMqttSsl] = useState(false);

  const [showWarning, setShowWarning] = useState(false);

  // Preflight
  const [isLocpermiGiven, setISLocpermiGiven] = useState(false);
  const [isVpnOn, setIsVpnOn] = useState(true);
  const [isMobileDataOn, setISMobileDataOn] = useState(true);
  const mobileDataRef = useRef<HTMLDivElement | null>(null);
  const locationPermissionRef = useRef<HTMLDivElement | null>(null);
  const vpnRef = useRef<HTMLDivElement | null>(null);

  // Status
  const [connectedSsid, setConnectedSsid] = useState('');

  const [wifiNetworks, setWifiNetworks] = useState<WifiNetwork[]>([]);
  const wifiNetworksRef = useRef<WifiNetwork[]>([]);
  useEffect(() => { wifiNetworksRef.current = wifiNetworks; }, [wifiNetworks]);
  const [statusMessage, setStatusMessage] = useState('');

  // Shared popup (success / failed) — used for transient confirmations and errors.
  const [popupIsOpen, setPopupIsOpen] = useState(false);
  const [popupStatus, setPopupStatus] = useState<'success' | 'failed'>('failed');
  const [popupTitle, setPopupTitle] = useState('');
  const [popupSubtitle, setPopupSubtitle] = useState('');
  const [popupActionText, setPopupActionText] = useState<string | undefined>(
    undefined,
  );
  // `device` swaps the generic SuccessModal for the device-illustration popup.
  const [popupVariant, setPopupVariant] = useState<'default' | 'device'>(
    'default',
  );
  const [popupShowAlertIcon, setPopupShowAlertIcon] = useState(false);
  const popupOnActionRef = useRef<(() => void) | undefined>(undefined);

  // Auto-dismiss timer for transient success popups.
  const popupAutoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wi-Fi/Server confirmation popup (Step 3 → integrations gate).
  const [confirmPopupIsOpen, setConfirmPopupIsOpen] = useState(false);

  // Validation
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    wifiname: '',
    password: '',
  });

  // Polling interval for "wait for internet" — cleared on unmount.
  const internetIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // Track wifi attempt count and pending log data across retries
  const wifiAttemptRef = useRef(0);
  const pendingWifiLogRef = useRef<Array<{
    deviceId: string;
    timestamp: number;
    request: string;
    response: string;
    attemptNumber: number;
  }>>([]);

  // Resolver for the in-flight SEND_WIFI_CREDENTIALS RN bridge call.
  // RN replies via a SEND_WIFI_CREDENTIALS_RESULT message handled in the
  // message listener below.
  const sendCredentialsResolverRef = useRef<
    ((_value: { success: boolean; message?: string; error?: string }) => void) | null
  >(null);

  // Callback invoked on the next WIFI_CONNECT_RESULT when auto-connecting to
  // the IAQ_ device hotspot (CONNECT_WIFI action, not OPEN_WIFI_SETTINGS).
  const pendingDeviceConnectCallbackRef = useRef<((_success: boolean) => void) | null>(null);
  // Fallback for when auto-connect fails: holds the same connect callback so
  // that once the user joins the IAQ_ hotspot manually (via OPEN_WIFI_SETTINGS),
  // the DEVICE_WIFI_CONNECTED message can resume the flow and send Wi-Fi details.
  const pendingDeviceManualConnectCallbackRef = useRef<((_success: boolean) => void) | null>(null);
  // One-shot callback: when autoConnectToDeviceWifi triggers a fresh scan,
  // the WIFI_SCAN_RESULT handler calls this with the results.
  const pendingScanCallbackRef = useRef<((_networks: WifiNetwork[]) => void) | null>(null);

  // Callback invoked on the next WIFI_BINDING_RELEASED during the
  // reconnect-to-internet phase (after /wifi credentials are sent to device).
  const pendingWifiConnectCallbackRef = useRef<(() => void) | null>(null);

  // Guard to prevent sendWifiDetails from being invoked more than once per
  // flow attempt. Both WIFI_CONNECT_RESULT (new auto path) and
  // DEVICE_WIFI_CONNECTED (old manual path) can fire for the same physical
  // event; without this guard the second trigger creates a duplicate polling
  // closure whose in-flight tick can fire logWifiConnection prematurely.
  const wifiDetailsSentRef = useRef(false);

  // Platform — iOS routes Wi-Fi credentials through the RN bridge (iOS blocks
  // raw HTTP to the ESP32 SoftAP); Android calls the device directly from web.
  // The ref is updated synchronously in the message handler so sendWifiDetails
  // always reads the correct value without waiting for a React render cycle.
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const isIOSDeviceRef = useRef(false);


  /* ---------------- Context ---------------- */

  const deviceContext = useContext(DevicesContext);
  if (!deviceContext) {
    throw new Error('useDeviceConfigFlow must be used within a DevicesProvider');
  }
  const {
    updateDeviceStatusBySerialNumber,
    updateDevice,
    SendWifiCredentials,
    deviceOffsetsData,
    logWifiConnection,
    devices
  } = deviceContext;

  /* ---------------- Navigation ---------------- */

  const goTo = useCallback(
    (next: StepKey) => {
      setStep(next);
    },
    [],
  );

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  const onClose = useCallback(() => {
    // Strip the flow's `model` param so the device-details page does not
    // keep stale config-flow state on its URL (which would otherwise look
    // like a reload on return).
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('model');
        return next;
      },
      { replace: true },
    );
    setShowConfigFlow(false);
  }, [setShowConfigFlow, setSearchParams]);

  const navigateToDashboard = useCallback(() => {
    // Trigger an immediate data refresh so the dashboard has fresh data
    // instead of waiting for the 5-minute polling interval.
    deviceContext.getLiveDataForDashboard();
    onClose();
    if (deviceId) {
      navigate(`/devices/${deviceId}`);
    } else {
      navigate('/devices');
    }
  }, [navigate, deviceId, onClose, deviceContext]);

  const navigateToAddDevice = useCallback(() => {
    onClose();
    navigate('/add-device');
  }, [navigate, onClose]);

  /* ---------------- URL ↔ step sync ---------------- */

  // Merge `model` into existing search params rather than replacing them —
  // the device-details page owns `view` / `tab` on the same URL and must not
  // be wiped when the flow opens or advances steps.
  const setModelParam = useCallback(
    (model: StepKey, replace: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('model', model);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const view = searchParams.get('model') as StepKey | null;
    if (!view) {
      setModelParam('on', true);
      return;
    }
    if (!VALID_STEPS.includes(view)) {
      setModelParam('on', true);
      return;
    }
    if (view !== stepRef.current) {
      setStep(view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const view = searchParams.get('model');
    if (view === step) return;
    setModelParam(step, step === 'on' || step === 'done' || step === 'failed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /* ---------------- BACnet IDs derived from serial ---------------- */

  useEffect(() => {
    const serialSuffix = SerialNumber.slice(-4);
    const parsedDeviceId = Number.parseInt(serialSuffix, 10);
    const idStr = Number.isNaN(parsedDeviceId) ? '' : String(parsedDeviceId);

    setBacnetDeviceId(idStr);
    setModbusDeviceId(idStr);
    setBacnetDeviceName(`SE100 ${serialSuffix}`);
  }, [SerialNumber]);

  /* ---------------- Helpers ---------------- */

  const clearAutoDismiss = useCallback(() => {
    if (popupAutoDismissRef.current) {
      clearTimeout(popupAutoDismissRef.current);
      popupAutoDismissRef.current = null;
    }
  }, []);

  const closePopup = useCallback(() => {
    clearAutoDismiss();
    setPopupIsOpen(false);
    popupOnActionRef.current = undefined;
  }, [clearAutoDismiss]);

  const showPopup = useCallback(
    (opts: {
      status: 'success' | 'failed';
      title: string;
      subtitle: string;
      actionText?: string;
      onAction?: () => void;
      autoDismissMs?: number;
      variant?: 'default' | 'device';
      showAlertIcon?: boolean;
    }) => {
      clearAutoDismiss();
      setPopupStatus(opts.status);
      setPopupTitle(opts.title);
      setPopupSubtitle(opts.subtitle);
      setPopupActionText(opts.actionText);
      setPopupVariant(opts.variant ?? 'default');
      setPopupShowAlertIcon(opts.showAlertIcon ?? false);
      popupOnActionRef.current = opts.onAction;
      setPopupIsOpen(true);

      if (opts.autoDismissMs && opts.autoDismissMs > 0) {
        popupAutoDismissRef.current = setTimeout(() => {
          setPopupIsOpen(false);
          const cb = popupOnActionRef.current;
          popupOnActionRef.current = undefined;
          cb?.();
        }, opts.autoDismissMs);
      }
    },
    [clearAutoDismiss],
  );

  const showFailurePopup = useCallback(
    (title: string, subtitle: string, variant: 'default' | 'device' = 'default') => {
      showPopup({ status: 'failed', title, subtitle, variant });
    },
    [showPopup],
  );

  // Clear any pending auto-dismiss on unmount.
  useEffect(() => clearAutoDismiss, [clearAutoDismiss]);

  const checkInternet = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      // Append a timestamp to bust any WebView-level cache so we don't get a
      // stale 204 from when the phone was previously on an internet network.
      await fetch(`https://www.google.com/generate_204?t=${Date.now()}`, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
      });
      clearTimeout(timer);
      return true;
    } catch {
      clearTimeout(timer);
      return false;
    }
  }, []);

  const checkDevicePing = useCallback(
    (attempt: number): Promise<boolean> => {
      const MAX_ATTEMPTS = 3;
      const REQUEST_TIMEOUT = 30000;
      const RETRY_DELAY = 10000;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      return fetch('http://192.168.4.1/ping', {
        method: 'GET',
        signal: controller.signal,
      })
        .then((res) => {
          clearTimeout(timer);
          if (res.status !== 200) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then((json) => {
          if (json?.status === 'ok') return true as boolean;
          throw new Error('Unexpected ping response');
        })
        .catch((): Promise<boolean> | boolean => {
          clearTimeout(timer);
          if (attempt >= MAX_ATTEMPTS) return false;
          return new Promise<boolean>((resolve) => {
            setTimeout(
              () => resolve(checkDevicePing(attempt + 1)),
              RETRY_DELAY,
            );
          });
        });
    },
    [],
  );

  /* ---------------- Send Wi-Fi details to device ---------------- */

  const sendWifiDetails = useCallback(
    async (ssid: string, password: string) => {
      // Prevent duplicate invocations within the same flow attempt. Both
      // WIFI_CONNECT_RESULT and DEVICE_WIFI_CONNECTED can fire for the same
      // physical network event; only the first one should proceed.
      if (wifiDetailsSentRef.current) return;
      wifiDetailsSentRef.current = true;
      startLoading();
      const payload: SendWifiCredentialsRequest = {
        ssid,
        password,
        deviceid: SerialNumber,
        devicePassword: '',
        offsets:
          deviceOffsetsData.length > 0
            ? deviceOffsetsData.map((d) => ({
                param: d.parameterName,
                x: d.offsetX,
                y: d.offsetY,
              }))
            : DEFAULT_OFFSETS,
        serverUrl,
        gateWayKey: '',
        ...(() => {
          const bmsActive = featureBmsEnabled && enableBms;
          const useBacnet = bmsActive && bmsProtocol === 'bacnet';
          const useModbus = bmsActive && bmsProtocol === 'modbus';
          return {
            enablebms: bmsActive,
            enablebacnet: useBacnet,
            bacnetDeviceId:
              useBacnet && bacnetDeviceId.trim()
                ? Number(bacnetDeviceId)
                : undefined,
            bacnetDeviceName:
              useBacnet && bacnetDeviceName.trim()
                ? bacnetDeviceName
                : undefined,
            bacnetPort:
              useBacnet && bacnetPort.trim() ? Number(bacnetPort) : undefined,
            modbusPort:
              useModbus && modbusPort.trim() ? Number(modbusPort) : undefined,
            modbusDeviceId:
              useModbus && modbusDeviceId.trim()
                ? Number(modbusDeviceId)
                : undefined,
            modbusConnectionType: useModbus ? modbusConnectionType : undefined,
          };
        })(),
        ...(() => {
          const mqttActive = featureMqttEnabled && enableMqtt;
          return {
            enablemqtt: mqttActive,
            mqttHost:
              mqttActive && mqttHost.trim() ? mqttHost.trim() : undefined,
            mqttPort:
              mqttActive && mqttPort.trim() ? Number(mqttPort) : undefined,
            mqttSsl: mqttActive ? mqttSsl : undefined,
          };
        })(),
        timestamp: Math.floor(Date.now() / 1000),
      };

      try {
        wifiAttemptRef.current += 1;
        const currentAttempt = wifiAttemptRef.current;
        // iOS blocks raw HTTP to the ESP32 SoftAP from a webview, so we route
        // through the RN bridge — RN pins the request to the SoftAP and
        // replies via SEND_WIFI_CREDENTIALS_RESULT (handled above). Android
        // can hit the device directly from the webview.
        const result = isIOSDeviceRef.current
          ? await new Promise<{
              success: boolean;
              message?: string;
              error?: string;
            }>((resolve) => {
              sendCredentialsResolverRef.current = resolve;
              window.ReactNativeWebView?.postMessage(
                JSON.stringify({ action: 'SEND_WIFI_CREDENTIALS', payload }),
              );
            })
          : await SendWifiCredentials(payload);

        // Store log data — can't send now because phone is on device hotspot (no internet)
        const logEntry = {
          deviceId: SerialNumber,
          timestamp: payload.timestamp,
          request: JSON.stringify({ ...payload, password: undefined }),
          response: JSON.stringify(result),
          attemptNumber: currentAttempt,
        };
        pendingWifiLogRef.current.push(logEntry);

        if (result.success) {
          const data: UpdateDeviceStatusRequest = {
            serialNumber: SerialNumber,
            onboardingStatus: 'WIFICONNECTED',
            operationalStatus: isFirstOnboardingRef.current ? 'INITIALIZE' : 'ONLINE',
            connectedWifiNetworkName: ssid,
          };

          setConnectedSsid(ssid);

          const startInternetPolling = () => {
            setStatusMessage('Connecting to internet…');
            if (internetIntervalRef.current) {
              clearInterval(internetIntervalRef.current);
              internetIntervalRef.current = null;
            }

            // Extract the save-device logic so it can be called from both
            // the normal path (internet available after 10 s) and the fallback
            // polling path (user manually reconnects on the 'wait' screen).
            const proceed = async () => {
              setStatusMessage('Saving device data…');

              const MAX_ATTEMPTS = 3;
              const RETRY_DELAY_MS = 3000;

              // Save wifi connection logs — non-fatal, log errors and continue.
              const logsToSend = [...pendingWifiLogRef.current];
              pendingWifiLogRef.current = [];
              for (const log of logsToSend) {
                let logLastError: unknown;
                for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                  try {
                    await logWifiConnection(log);
                    logLastError = undefined;
                    break;
                  } catch (e) {
                    logLastError = e;
                    if (attempt < MAX_ATTEMPTS) {
                      await new Promise<void>((resolve) => { setTimeout(resolve, RETRY_DELAY_MS); });
                    }
                  }
                }
                if (logLastError) {
                  console.error('logWifiConnection failed after retries:', logLastError);
                }
              }

              let lastError: unknown;

              for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                  const response = await updateDeviceStatusBySerialNumber(data);
                  if (response.success) {
                    setStatusMessage('Device connected successfully');
                    stopLoading();
                    showPopup({
                      status: 'success',
                      title: 'Device Connected Successfully',
                      subtitle: 'Your SmartEco device is now monitoring your air quality.',
                      actionText: 'Continue',
                      onAction: () => goTo('done'),
                      variant: 'device'
                    });
                    return;
                  }
                  lastError = new Error(response.message ?? 'Non-success response');
                } catch (e) {
                  lastError = e;
                }

                if (attempt < MAX_ATTEMPTS) {
                  setStatusMessage(`Saving device data… (retry ${attempt})`);
                  await new Promise<void>((resolve) => {
                    internetIntervalRef.current = setTimeout(() => {
                      internetIntervalRef.current = null;
                      resolve();
                    }, RETRY_DELAY_MS) as unknown as ReturnType<typeof setInterval>;
                  });
                }
              }

              console.error('updateDeviceStatusBySerialNumber failed after retries:', lastError);
              showFailurePopup(
                'Unable to Update Device Status',
                "We couldn't update the device status. Please check your connection and try again.",
              );
              goTo('failed');
              stopLoading();
            };

            // Wait 10 s for the OS routing table and WebView socket pool to
            // fully switch over to the internet network, then check connectivity.
            internetIntervalRef.current = setTimeout(async () => {
              internetIntervalRef.current = null;

              const hasInternet = await checkInternet();
              if (!hasInternet) {
                // No internet yet — show the manual connect screen and poll
                // silently until the user reconnects.
                stopLoading();
                goTo('wait');
                const poll = setInterval(async () => {
                  const online = await checkInternet();
                  if (online) {
                    clearInterval(poll);
                    internetIntervalRef.current = null;
                    startLoading();
                    await proceed();
                  }
                }, 5000) as unknown as ReturnType<typeof setInterval>;
                internetIntervalRef.current = poll;
                return;
              }

              await proceed();
            }, 10000) as unknown as ReturnType<typeof setInterval>;
          };

          // Tell the native bridge to release the IoT AP network binding and
          // reconnect to the internet WiFi. The native side calls
          // forceWifiUsageWithOptions(false) + bindProcessToNetwork and then
          // sends WIFI_BINDING_RELEASED once the process is rebound to the
          // internet network. On web/desktop (no RN bridge) fall straight
          // through to polling.
          if (window.ReactNativeWebView) {
            pendingWifiConnectCallbackRef.current = startInternetPolling;
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ action: 'RELEASE_WIFI_BINDING', ssid, password })
            );
          } else {
            startInternetPolling();
          }
        } else {
          showPopup({
            status: 'failed',
            title: 'Unable to Connect to Device',
            subtitle: result.message?.includes("Internet") ? result.message : "We couldn't send your Wi-Fi details to the device. Please ensure you're still connected to the device IAQ_ network and try again.",
            actionText: 'Continue',
            onAction: () => goTo('failed'),
            variant: 'device'
          });
          stopLoading();
        }
      } catch (error) {
        // Keep diagnostic logging for native bridge issues.
        // eslint-disable-next-line no-console
        console.error('Error sending WiFi data:', error);
        stopLoading();
      }
    },
    [
      SerialNumber,
      serverUrl,
      deviceOffsetsData,
      enableBms,
      bmsProtocol,
      bacnetDeviceId,
      bacnetDeviceName,
      bacnetPort,
      modbusPort,
      modbusDeviceId,
      modbusConnectionType,
      enableMqtt,
      mqttHost,
      mqttPort,
      mqttSsl,
      SendWifiCredentials,
      logWifiConnection,
      updateDeviceStatusBySerialNumber,
      goTo,
      showFailurePopup,
      showPopup,
      checkInternet,
      startLoading,
      stopLoading,
      setStatusMessage,
    ],
  );

  // Always-current reference to sendWifiDetails — used by the RN message
  // handler (which can't hold sendWifiDetails in its own dep array).
  const sendWifiDetailsRef = useRef(sendWifiDetails);
  useEffect(() => { sendWifiDetailsRef.current = sendWifiDetails; }, [sendWifiDetails]);

  // Automatically connect the phone to the IAQ_ device hotspot via the RN bridge.
  // CONNECT_WIFI replies with WIFI_CONNECT_RESULT (not DEVICE_WIFI_CONNECTED),
  // so we register a one-shot callback on pendingDeviceConnectCallbackRef to
  // handle success/failure when that result arrives.
  const autoConnectToDeviceWifi = useCallback(() => {
    // Reset the guard so this fresh attempt can call sendWifiDetails.
    wifiDetailsSentRef.current = false;
    // Clear any stale manual-connect callback left over from a previous
    // attempt so a late DEVICE_WIFI_CONNECTED can't fire an old closure.
    pendingDeviceManualConnectCallbackRef.current = null;

    const connectTo = (ssid: string) => {
      startLoading();
      setStatusMessage('Connecting to device Wi-Fi…');
      pendingDeviceConnectCallbackRef.current = (success: boolean) => {
        if (success) {
          setStatusMessage('Device hotspot connected');
          setTimeout(() => {
            setStatusMessage('Sending Wi-Fi details…');
            sendWifiDetailsRef.current(wifinnameRef.current, wifipasswordRef.current);
          }, 1500);
        } else {
          stopLoading();
          goTo('connect');
        }
      };
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ action: 'CONNECT_WIFI', ssid, password: '12345678', serialNumber: SerialNumber })
      );
    };

    // On both Android and iOS, connect directly to the device hotspot named
    // after the serial number (IAQ_<SerialNumber>) without scanning first.
    connectTo(`IAQ_${SerialNumber}`);

    // On Android: trigger a fresh Wi-Fi scan and wait for results before matching.
    // Previously we read wifiNetworksRef which was often empty because SCAN_WIFI
    // had never been triggered or results hadn't arrived yet.
    // if (!isIOSDevice) {
    //   startLoading();
    //   setStatusMessage('Connecting to device Wi-Fi…');

    //   const matchAndConnect = (networks: WifiNetwork[]) => {
    //     const exactMatch = networks.find(n => n.SSID === `IAQ_${SerialNumber}`);
    //     const fallbackMatch = networks.find(n => n.SSID.includes('IAQ_'));
    //     const ssidToConnect = exactMatch?.SSID ?? fallbackMatch?.SSID;

    //     if (!ssidToConnect) {
    //       stopLoading();
    //       showPopup({
    //         status: 'failed',
    //         title: 'Device Not Found',
    //         subtitle: "Could not find the SmartEco device hotspot. Make sure the device is powered on and nearby, then try again.",
    //         actionText: 'OK',
    //         onAction: () => goTo('on'),
    //         showAlertIcon: true,
    //       });
    //       return;
    //     }

    //     connectTo(ssidToConnect);
    //   };

    //   // Register callback so WIFI_SCAN_RESULT handler delivers results here.
    //   pendingScanCallbackRef.current = matchAndConnect;

    //   // Timeout: if scan results don't arrive within 15s, fail gracefully
    //   // instead of leaving the user stuck on the loading screen forever.
    //   const scanTimeout = setTimeout(() => {
    //     if (pendingScanCallbackRef.current) {
    //       pendingScanCallbackRef.current = null;
    //       stopLoading();
    //       showPopup({
    //         status: 'failed',
    //         title: 'Scan Timed Out',
    //         subtitle: "Wi-Fi scan did not return results. Make sure the device is powered on and nearby, then try again.",
    //         actionText: 'OK',
    //         onAction: () => goTo('on'),
    //         showAlertIcon: true,
    //       });
    //     }
    //   }, 15000);

    //   // Clear timeout if scan results arrive in time.
    //   const originalCallback = matchAndConnect;
    //   pendingScanCallbackRef.current = (networks: WifiNetwork[]) => {
    //     clearTimeout(scanTimeout);
    //     originalCallback(networks);
    //   };

    //   // Trigger the scan.
    //   window.ReactNativeWebView?.postMessage(
    //     JSON.stringify({ action: 'SCAN_WIFI' }),
    //   );
    //   return;
    // }

    // Get device details first and get wifiname from that to check whether the device is already connected to wifi or not
    // iOS: cannot enumerate networks; attempt the serial-number SSID directly.
    // const deviceDetails = devices.find(d => d.serialNumber == SerialNumber);
    // if (deviceDetails?.wifiNetworkName) {
    //   connectTo(`IAQ_${SerialNumber}`);
    // } else {
    //   connectTo('IAQ_');
    // }
  }, [SerialNumber, isIOSDevice, startLoading, stopLoading, showPopup, goTo, setStatusMessage]);

  /* ---------------- Validation ---------------- */

  // Step 3 owns these — Wi-Fi name + password only.
  const checkWifiCredentials = useCallback((): ValidationErrors => {
    const errors: ValidationErrors = {};
    if (!wifiname.trim()) {
      errors.wifiname = 'Please enter your Wi-Fi network name.';
    }
    if (!wifipassword.trim()) {
      errors.password = 'Please enter your Wi-Fi password.';
    }
    setValidationErrors((prev) => ({
      ...prev,
      wifiname: errors.wifiname,
      password: errors.password,
    }));
    return errors;
  }, [wifiname, wifipassword]);

  // Step 4 owns these — only checked when the user actually enables BMS/MQTT.
  const checkIntegrations = useCallback((): ValidationErrors => {
    const errors: ValidationErrors = {};
    if (featureBmsEnabled && enableBms) {
      if (bmsProtocol === 'bacnet') {
        if (!bacnetDeviceId.trim() || Number.isNaN(Number(bacnetDeviceId))) {
          errors.bms = 'Enter a valid BACnet device ID.';
        }
      } else {
        const badModbusId =
          !modbusDeviceId.trim() || Number.isNaN(Number(modbusDeviceId));
        const badModbusPort =
          !modbusPort.trim() || Number.isNaN(Number(modbusPort));
        if (badModbusId && badModbusPort) {
          errors.bms = 'Enter valid Modbus device ID and port.';
        } else if (badModbusId) {
          errors.bms = 'Enter a valid Modbus device ID.';
        } else if (badModbusPort) {
          errors.bms = 'Enter a valid Modbus port.';
        }
      }
    }
    if (featureMqttEnabled && enableMqtt) {
      if (!mqttHost.trim()) {
        errors.mqtt = 'Enter the MQTT broker host.';
      } else if (!mqttPort.trim() || Number.isNaN(Number(mqttPort))) {
        errors.mqtt = 'Enter a valid MQTT port.';
      }
    }
    setValidationErrors((prev) => ({
      ...prev,
      bms: errors.bms,
      mqtt: errors.mqtt,
    }));
    return errors;
  }, [
    enableBms,
    bmsProtocol,
    bacnetDeviceId,
    modbusDeviceId,
    modbusPort,
    enableMqtt,
    mqttHost,
    mqttPort,
  ]);

  // Step 3 → Next: validate Wi-Fi fields, run internet pre-check, then open
  // the confirmation popup. Confirm advances to the integrations step.
  const verify = useCallback(async () => {
    const errors = checkWifiCredentials();
    if (Object.keys(errors).length > 0) return;
    startLoading();

    const isOnline = await checkInternet();
    stopLoading();
    if (isOnline) {
      setConfirmPopupIsOpen(true);
    } else {
      showFailurePopup(
        'No Internet Connection',
        "Your phone isn't connected to the internet. Please check your Wi-Fi connection and try again.",
      );
    }
  }, [
    checkWifiCredentials,
    checkInternet,
    showFailurePopup,
    startLoading,
    stopLoading,
  ]);

  const closeConfirmPopup = useCallback(() => {
    setConfirmPopupIsOpen(false);
  }, []);

  const confirmWifiDetails = useCallback(() => {
    setConfirmPopupIsOpen(false);
    // Skip integrations step if both BMS and MQTT features are disabled
    if (!featureBmsEnabled && !featureMqttEnabled) {
      goTo('deviceWifi');
    } else {
      goTo('integrations');
    }
  }, [goTo]);

  // Step 4 → Skip / Next: validate integrations only if toggled on.
  const proceedFromIntegrations = useCallback(() => {
    const errors = checkIntegrations();
    if (Object.keys(errors).length > 0) return;
    goTo('deviceWifi');
  }, [checkIntegrations, goTo]);

  const handleEnableLocation = useCallback(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'OPEN_APP_SETTINGS' }),
    );
  }, []);

  // Reads the phone's GPS position once location permission is granted and
  // persists it on the device record (Latitude/Longitude). Guarded so it runs
  // at most once per flow and never overlaps an in-flight request.
  const requestingLocationRef = useRef(false);
  const locationUpdatedRef = useRef(false);

  const getUserCurrentLocation = useCallback(() => {
    // Need a device record to attach the coordinates to.
    if (!deviceId) return;
    const deviceDetails = devices.find(d => d.deviceId == Number(deviceId))
    // Prevent duplicate / simultaneous location updates.
    if (requestingLocationRef.current || locationUpdatedRef.current) return;
    if (!navigator.geolocation) {
      // eslint-disable-next-line no-console
      console.log('Geolocation is not supported by this browser');
      return;
    }

    requestingLocationRef.current = true;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        // Prepare update data with proper image handling
        const updateData: UpdateDeviceRequest = {
          DeviceName: deviceDetails?.deviceName,
          DeviceLocationType: deviceDetails?.deviceLocationType,
          DeviceTypeId: deviceDetails?.deviceTypeId,
          SerialNumber: deviceDetails?.serialNumber,
          InstallationLocation: deviceDetails?.installationLocation,
          Description: deviceDetails?.description,
          Latitude: lat,
          Longitude: lng,
          DeviceImageUrl: deviceDetails?.deviceImageUrl,
          SiteId: deviceDetails?.siteId,
          // EnvironmentMode: formData.EnvironmentMode,
        };
        try {
          const response = await updateDevice(deviceId, updateData);
          if (response?.success) {
            locationUpdatedRef.current = true;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('Failed to update device location:', error);
        } finally {
          requestingLocationRef.current = false;
        }
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.log('Location access denied or unavailable:', error.message);
        requestingLocationRef.current = false;
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  }, [deviceId, updateDevice]);

  // Step 2 ('set'): as soon as location permission is reported as granted —
  // whether it was already on when the user reached this step, or they enabled
  // it afterwards (SYSTEM_STATUS flips isLocpermiGiven) — capture the current
  // location and save it to the device.
  useEffect(() => {
    if (step === 'set' && isLocpermiGiven) {
      getUserCurrentLocation();
    }
  }, [step, isLocpermiGiven, getUserCurrentLocation]);

  const handleOpenSettings = useCallback(() => {
    startLoading();
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'OPEN_WIFI_SETTINGS' }),
    );
  }, [startLoading]);

  const [scanningWifi, setScanningWifi] = useState(false);
  // Native often returns cached scan results almost instantly, which makes the
  // spinner flash and vanish before the user perceives it. We enforce a minimum
  // visible loading time so every rescan reliably shows "loading → results".
  const MIN_SCAN_MS = 800;
  const scanStartRef = useRef(0);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rescanWifi = useCallback(() => {
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    scanStartRef.current = Date.now();
    setScanningWifi(true);
    setWifiNetworks([]);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'SCAN_WIFI' }),
    );
  }, []);

  /* ---------------- React Native WebView messages ---------------- */

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.action === 'WIFI_SCAN_RESULT') {
         // console.log('[DEBUG WIFI_SCAN_RESULT] raw networks:', JSON.stringify(data.networks));
          const networks = data.networks;
          const elapsed = Date.now() - scanStartRef.current;
          const remaining = Math.max(0, MIN_SCAN_MS - elapsed);
          if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
          scanTimerRef.current = setTimeout(() => {
            setWifiNetworks(networks);
            setScanningWifi(false);
            scanTimerRef.current = null;
          }, remaining);
          // If autoConnectToDeviceWifi is waiting for scan results, deliver them.
          if (pendingScanCallbackRef.current) {
            const cb = pendingScanCallbackRef.current;
            pendingScanCallbackRef.current = null;
            cb(networks);
          }
        } else if (data.action === 'IS_IOS') {
          isIOSDeviceRef.current = !!data.isIOS;
          setIsIOSDevice(!!data.isIOS);
        } else if (data.action === 'SYSTEM_STATUS') {
          const system = data.data;
          setISLocpermiGiven(system?.locationPermission);
          setISMobileDataOn(system?.mobileData);
          setIsVpnOn(system?.isVpnOn);

          // if (
          //   !system?.mobileData &&
          //   !system?.isVpnOn &&
          //   system?.locationPermission &&
          //   stepRef.current === 'set'
          // ) {
          //   window.ReactNativeWebView?.postMessage(
          //     JSON.stringify({ action: 'SCAN_WIFI' }),
          //   );
          //   setStep('wificonf');
          // }
        } else if (data.action === 'WIFI_CONNECT_RESULT') {
          if (pendingDeviceConnectCallbackRef.current) {
            // This result is for the auto-connect to the IAQ_ device hotspot.
            const cb = pendingDeviceConnectCallbackRef.current;
            pendingDeviceConnectCallbackRef.current = null;
            if (!data.success) pendingDeviceManualConnectCallbackRef.current = cb;
            cb(!!data.success);
          } else if (data.success) {
            const scannedSsid = data.currentWifi?.ssid ?? '';
            setWifiName(scannedSsid);
          } else {
            setShowWarning(true);
          }
        } else if (data.action === 'WIFI_BINDING_RELEASED') {
          // Native side has unbound the process from the IoT AP and rebound
          // it to the internet network. Fire the pending callback (which is
          // startInternetPolling) so we proceed to save the device.
          if (pendingWifiConnectCallbackRef.current) {
            const cb = pendingWifiConnectCallbackRef.current;
            pendingWifiConnectCallbackRef.current = null;
            cb();
          }
        } else if (data.action === 'SEND_WIFI_CREDENTIALS_RESULT') {
          const resolver = sendCredentialsResolverRef.current;
          sendCredentialsResolverRef.current = null;
          resolver?.({
            success: !!data.success,
            message: data.message,
            error: data.error,
          });
        } else if (data.action === 'DEVICE_WIFI_CONNECTED') {
            if (pendingDeviceManualConnectCallbackRef.current) {
              // Manual fallback: the user connected to the IAQ_ hotspot by hand
              // after the auto-connect failed. Reuse the original connect
              // callback to resume the flow (send Wi-Fi details on success).
              const cb = pendingDeviceManualConnectCallbackRef.current;
              if (data.success) pendingDeviceManualConnectCallbackRef.current = null;
              cb(!!data.success);

              if (!data.success) {
                showFailurePopup(
                  'You are not connected to Device Wi-Fi',
                  'Connection failed. Please try again manually.',
                );
              }
            } else {
              // No manual connect pending — nothing to resume. Clear the
              // loading state so the UI can't hang on a stray message.
              stopLoading();
            }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Invalid message from RN:', e);
      }
    };

    window.addEventListener('message', handler);
    // @ts-ignore - RN dispatches custom event
    document.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
      // @ts-ignore
      document.removeEventListener('message', handler);
    };
  }, [checkDevicePing, showFailurePopup, showPopup, stopLoading]);

  /* ---------------- Platform detection (iOS vs Android) ---------------- */

  useEffect(() => {
    if (window.ReactNativeWebView) {
      // Ask RN which platform we're on. Response arrives via the IS_IOS
      // message handled in the listener above.
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: 'GET_IS_IOS' }),
      );
      return;
    }
    // Web/desktop fallback — treat macOS browsers as iOS-like.
    const ua = navigator.userAgent.toLowerCase();
    if (navigator.platform.toLowerCase().includes('mac') || ua.includes('mac')) {
      isIOSDeviceRef.current = true;
      setIsIOSDevice(true);
    }
  }, []);

  /* ---------------- Cleanup polling on unmount ---------------- */

  useEffect(() => {
    return () => {
      if (internetIntervalRef.current) {
        clearInterval(internetIntervalRef.current);
        internetIntervalRef.current = null;
      }
    };
  }, []);

  /* ---------------- Exit confirmation modal ---------------- */

  const [exitModalOpen, setExitModalOpen] = useState(false);

  const openExitModal = useCallback(() => {
    setExitModalOpen(true);
  }, []);

  const closeExitModal = useCallback(() => {
    setExitModalOpen(false);
  }, []);

  const exitSetup = useCallback(() => {
    setExitModalOpen(false);
    onClose();
    navigate('/devices');
  }, [onClose, navigate]);

  /* ---------------- Public ctx ---------------- */

  const ctx: FlowCtx = {
    step,
    goTo,
    goBack,
    onClose,
    serialNumber: SerialNumber,
    deviceId,

    wifiname,
    setWifiName,
    wifipassword,
    setWifiPassword,
    serverUrl,
    setServerUrl,

    enableBms,
    setEnableBms,
    bmsProtocol,
    setBmsProtocol,
    bacnetDeviceId,
    setBacnetDeviceId,
    bacnetDeviceName,
    setBacnetDeviceName,
    bacnetPort,
    setBacnetPort,
    modbusPort,
    setModbusPort,
    modbusDeviceId,
    setModbusDeviceId,
    modbusConnectionType,
    setModbusConnectionType,

    enableMqtt,
    setEnableMqtt,
    mqttHost,
    setMqttHost,
    mqttPort,
    setMqttPort,
    mqttSsl,
    setMqttSsl,

    showWarning,
    validationErrors,
    setValidationErrors,

    wifiNetworks,
    rescanWifi,
    scanningWifi,

    isMobileDataOn,
    isLocpermiGiven,
    isVpnOn,
    mobileDataRef,
    locationPermissionRef,
    vpnRef,

    loading,
    isFirstOnboarding,
    connectedSsid,
    statusMessage,
    popupIsOpen,

    sendWifiDetails,
    autoConnectToDeviceWifi,
    verify,
    confirmWifiDetails,
    proceedFromIntegrations,
    handleEnableLocation,
    handleOpenSettings,
    navigateToDashboard,
    navigateToAddDevice,

    exitModalOpen,
    openExitModal,
    closeExitModal,
    exitSetup,

    isIOSDevice,

    featureBmsEnabled,
    featureMqttEnabled,
  };

  return {
    ctx,
    popup: {
      isOpen: popupIsOpen,
      status: popupStatus,
      title: popupTitle,
      subtitle: popupSubtitle,
      actionText: popupActionText,
      onAction: popupOnActionRef.current,
      variant: popupVariant,
      showAlertIcon: popupShowAlertIcon,
    },
    confirmPopup: {
      isOpen: confirmPopupIsOpen,
    },
    closePopup,
    closeConfirmPopup,
  };
}
